import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { TrendingUp } from 'lucide-react';
import { useAuth } from '../../../../services/authentication/authContext';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../../../stores/appUserDefaultStore';
import { canAccessReportsPage } from '../../../../services/accessManagement/accessManagement';

/**
 * Access + scope gate shared by the reports hub and every report detail page.
 * Denies when the account lacks `reports_page` (→ home). When `requireSingleRestaurant`
 * is set (cost-price/inventory reports that can't aggregate cleanly across
 * restaurants) and "all restaurants" is selected, it shows a "pick a single
 * restaurant" notice instead of the report. The hub and the order/revenue-driven
 * reports render in all-restaurants mode. Mirrors `InventoryPage.tsx:132-148`.
 */
export const ReportsGate: React.FC<{
  children: React.ReactNode;
  requireSingleRestaurant?: boolean;
  /** Set false when the caller is a report exposed outside the hub with its own
   * access rules (e.g. the standalone Product Utilization page under Cost Price,
   * open to every non-invoice-only account), so the report can be shown to
   * accounts that don't have `reports_page`. */
  requireReportsAccess?: boolean;
}> = ({
  children,
  requireSingleRestaurant = false,
  requireReportsAccess = true,
}) => {
  const { t } = useTranslation();
  const { backendUser, accountSettings } = useAuth();
  const selectedRestaurant = useAppUserDefaultStore((s) => s.restaurant);
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);

  if (requireReportsAccess && !canAccessReportsPage(backendUser, accountSettings)) return <Navigate to="/" />;

  if (requireSingleRestaurant && isAllRestaurants) {
    return (
      <div className="p-4 max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp className="w-8 h-8 text-green-600 dark:text-green-400" />
          <h1 className="text-3xl font-bold text-textPrimary dark:text-textPrimary-dark">{t('reports.hub.title')}</h1>
        </div>
        <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-yellow-800 dark:text-yellow-200">{t('reports.hub.selectSingleRestaurantForReport')}</p>
        </div>
      </div>
    );
  }

  if (!selectedRestaurant?.id) return null;

  return <>{children}</>;
};

export default ReportsGate;
